import { useEffect, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Layout } from "@/components/layout/Layout";
import { motion } from "framer-motion";
import { Calendar, Clock, ArrowRight, ArrowLeft, Share2, Facebook, Linkedin, Twitter, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getPostBySlug, getRelatedPosts, isNewPost, BlogSection, BlogPost as BlogPostType, getFallbackImageUrl } from "@/data/blogPosts";
import { blogPostsEn, categoryMapHeToEn } from "@/data/blogPostsEn";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";

export default function BlogPost() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('blog');
  const isRTL = i18n.language === 'he';
  const post = slug ? getPostBySlug(slug) : undefined;
  const enTranslation = slug && !isRTL ? blogPostsEn[slug] : undefined;

  // Get translated post data
  const translatedPost = useMemo(() => {
    if (!post) return undefined;
    if (isRTL) return post;
    if (!enTranslation) {
      return {
        ...post,
        title: "Article coming soon (English)",
        metaTitle: "Workplace Safety Article | Adam Safety",
        metaDescription: "This article is not yet available in English.",
        excerpt: "This article is not yet available in English.",
        category: categoryMapHeToEn[post.category] || "General",
        readTime: "",
        imageAlt: "Workplace safety article",
        keywords: ["workplace safety"],
        content: [
          { type: "p", text: "This article is not yet available in English." },
          { type: "cta", text: "Want the English version sooner? Contact us and we’ll prioritize it." },
        ] as BlogSection[],
      };
    }
    return {
      ...post,
      title: enTranslation.title,
      metaTitle: enTranslation.metaTitle,
      metaDescription: enTranslation.metaDescription,
      excerpt: enTranslation.excerpt,
      category: enTranslation.category,
      readTime: enTranslation.readTime,
      imageAlt: enTranslation.imageAlt,
      keywords: enTranslation.keywords,
      content: enTranslation.content as BlogSection[]
    };
  }, [post, enTranslation, isRTL]);

  // Generate table of contents from H2 headings
  const tableOfContents = useMemo(() => {
    if (!translatedPost) return [];
    return translatedPost.content
      .filter((section): section is BlogSection & { type: "h2"; text: string } => section.type === "h2" && !!section.text)
      .map((section, index) => ({
        id: `section-${index}`,
        title: section.text,
      }));
  }, [translatedPost]);

  const relatedPosts = post ? getRelatedPosts(post) : [];

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  // Get translated category for display
  const getDisplayCategory = (hebrewCategory: string): string => {
    if (isRTL) return hebrewCategory;
    return categoryMapHeToEn[hebrewCategory] || hebrewCategory;
  };

  // Get translated related post
  const getTranslatedRelatedPost = (relatedPost: BlogPostType) => {
    if (isRTL) return relatedPost;
    const enTranslation = blogPostsEn[relatedPost.slug];
    if (enTranslation) {
      return {
        ...relatedPost,
        title: enTranslation.title,
        category: enTranslation.category
      };
    }
    return {
      ...relatedPost,
      title: "Article coming soon (English)",
      category: categoryMapHeToEn[relatedPost.category] || relatedPost.category
    };
  };

  if (!post || !translatedPost) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-foreground mb-4">{t('post.notFound.title')}</h1>
            <p className="text-muted-foreground mb-6">{t('post.notFound.description')}</p>
            <Button onClick={() => navigate("/blog")} className="gap-2">
              {t('post.backToBlog')} {isRTL ? <ArrowLeft className="w-4 h-4" /> : <ArrowRight className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const shareTitle = translatedPost.title;

  const handleShare = (platform: "facebook" | "twitter" | "linkedin") => {
    const urls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareTitle)}`,
      linkedin: `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(shareTitle)}`,
    };
    window.open(urls[platform], "_blank", "width=600,height=400");
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // JSON-LD Schema for SEO
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: translatedPost.title,
    description: translatedPost.metaDescription,
    image: post.image,
    datePublished: post.date,
    dateModified: post.date,
    author: {
      "@type": "Organization",
      name: "Adam Safety",
      url: "https://adam-safe-hub.lovable.app",
    },
    publisher: {
      "@type": "Organization",
      name: "Adam Safety",
      logo: {
        "@type": "ImageObject",
         url: "https://adam-safety.com/icon-v2.png",
      },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": shareUrl,
    },
    keywords: translatedPost.keywords.join(", "),
  };

  const faqItems = translatedPost.content
    .filter((section) => section.type === "faq" && section.faqs?.length)
    .flatMap((section) => section.faqs ?? []);

  const faqSchema = faqItems.length
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqItems.map((faq) => ({
          "@type": "Question",
          name: faq.question,
          acceptedAnswer: { "@type": "Answer", text: faq.answer },
        })),
      }
    : null;

  let sectionIndex = -1;

  return (
    <Layout>
      <Helmet>
        <title>{translatedPost.metaTitle}</title>
        <meta name="description" content={translatedPost.metaDescription} />
        <meta name="keywords" content={translatedPost.keywords.join(", ")} />
        <link rel="canonical" href={shareUrl} />
        
        {/* Open Graph */}
        <meta property="og:title" content={translatedPost.metaTitle} />
        <meta property="og:description" content={translatedPost.metaDescription} />
        <meta property="og:image" content={post.image} />
        <meta property="og:url" content={shareUrl} />
        <meta property="og:type" content="article" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={translatedPost.metaTitle} />
        <meta name="twitter:description" content={translatedPost.metaDescription} />
        <meta name="twitter:image" content={post.image} />
        
        {/* Article specific */}
        <meta property="article:published_time" content={post.date} />
        <meta property="article:section" content={translatedPost.category} />
        {translatedPost.keywords.map((keyword, i) => (
          <meta key={i} property="article:tag" content={keyword} />
        ))}
        
        {/* JSON-LD Schema */}
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        {faqSchema && (
          <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        )}
      </Helmet>

      {/* Hero Section */}
      <section className="relative min-h-[50vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src={post.image} 
            alt={translatedPost.imageAlt} 
            className="w-full h-full object-cover"
            onError={(e) => {
              const target = e.currentTarget;
              if (!target.dataset.fallback) {
                target.dataset.fallback = "true";
                target.src = getFallbackImageUrl(post.id, post.category);
              }
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/80 to-transparent" />
        </div>
        
        <div className="container relative z-10 px-4 py-12">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <Link to="/blog" className="inline-flex items-center gap-2 text-muted-foreground hover:text-accent transition-colors mb-6">
              {isRTL ? <ArrowRight className="w-4 h-4" /> : <ArrowLeft className="w-4 h-4" />}
              {t('post.backToBlog')}
            </Link>
            
            <div className="flex flex-wrap gap-3 mb-4">
              <span className="px-3 py-1 text-sm font-semibold rounded-full bg-accent/20 text-accent">
                {translatedPost.category}
              </span>
              {isNewPost(post.date) && (
                <span className="px-3 py-1 text-sm font-bold rounded-full bg-accent text-accent-foreground">
                  {t('post.new')}
                </span>
              )}
              {post.featured && (
                <span className="px-3 py-1 text-sm font-bold rounded-full bg-primary text-primary-foreground">
                  {t('post.featured')}
                </span>
              )}
            </div>
            
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-6 max-w-4xl">
              {translatedPost.title}
            </h1>
            
            <div className="flex flex-wrap items-center gap-6 text-muted-foreground">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                {new Date(post.date).toLocaleDateString(isRTL ? "he-IL" : "en-US", { year: "numeric", month: "long", day: "numeric" })}
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                {translatedPost.readTime}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-12 bg-background">
        <div className="container px-4">
          <div className="grid lg:grid-cols-4 gap-12">
            {/* Sidebar - Table of Contents */}
            <aside className="lg:col-span-1 order-2 lg:order-1">
              <div className="sticky top-24">
                <motion.div
                  initial={{ opacity: 0, x: isRTL ? 20 : -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  className="bg-card border border-border rounded-2xl p-6"
                >
                  <h3 className="font-heading font-bold text-lg text-foreground mb-4">{t('post.tableOfContents')}</h3>
                  <nav className="space-y-2">
                    {tableOfContents.map((item, index) => (
                      <button
                        key={item.id}
                        onClick={() => scrollToSection(item.id)}
                        className={`block w-full ${isRTL ? 'text-right border-r-2 pr-3' : 'text-left border-l-2 pl-3'} text-sm text-muted-foreground hover:text-accent transition-colors py-1 border-transparent hover:border-accent`}
                      >
                        {item.title}
                      </button>
                    ))}
                  </nav>
                </motion.div>

                {/* Share Buttons */}
                <motion.div
                  initial={{ opacity: 0, x: isRTL ? 20 : -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                  className="bg-card border border-border rounded-2xl p-6 mt-6"
                >
                  <h3 className="font-heading font-bold text-lg text-foreground mb-4 flex items-center gap-2">
                    <Share2 className="w-5 h-5" />
                    {t('post.shareArticle')}
                  </h3>
                  <div className="flex gap-3">
                    <button
                      onClick={() => handleShare("facebook")}
                      className="p-3 rounded-full bg-[#1877F2] text-white hover:opacity-90 transition-opacity"
                      aria-label={t('share.facebook')}
                    >
                      <Facebook className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleShare("linkedin")}
                      className="p-3 rounded-full bg-[#0A66C2] text-white hover:opacity-90 transition-opacity"
                      aria-label={t('share.linkedin')}
                    >
                      <Linkedin className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleShare("twitter")}
                      className="p-3 rounded-full bg-[#1DA1F2] text-white hover:opacity-90 transition-opacity"
                      aria-label={t('share.twitter')}
                    >
                      <Twitter className="w-5 h-5" />
                    </button>
                  </div>
                </motion.div>
              </div>
            </aside>

            {/* Main Content */}
            <article className="lg:col-span-3 order-1 lg:order-2">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="prose prose-lg max-w-none"
              >
                {translatedPost.content.map((section, index) => {
                  if (section.type === "h2") {
                    sectionIndex++;
                    return (
                      <h2
                        key={index}
                        id={`section-${sectionIndex}`}
                        className="font-heading text-2xl md:text-3xl font-bold text-foreground mt-12 mb-6 scroll-mt-24"
                      >
                        {section.text}
                      </h2>
                    );
                  }
                  if (section.type === "h3") {
                    return (
                      <h3 key={index} className="font-heading text-xl md:text-2xl font-bold text-foreground mt-8 mb-4">
                        {section.text}
                      </h3>
                    );
                  }
                  if (section.type === "p") {
                    return (
                      <p key={index} className="text-muted-foreground leading-relaxed mb-6">
                        {section.text}
                      </p>
                    );
                  }
                  if (section.type === "list" && section.items) {
                    return (
                      <ul key={index} className={`space-y-3 mb-6 ${isRTL ? 'mr-6' : 'ml-6'}`}>
                        {section.items.map((item, i) => (
                          <li key={i} className="text-muted-foreground flex items-start gap-3">
                            <span className="w-2 h-2 rounded-full bg-accent mt-2.5 flex-shrink-0" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    );
                  }
                  if (section.type === "quote") {
                    return (
                      <blockquote key={index} className={`${isRTL ? 'border-r-4 pr-6 pl-4 rounded-l-lg' : 'border-l-4 pl-6 pr-4 rounded-r-lg'} border-accent bg-accent/10 py-4 my-8`}>
                        <p className="text-foreground font-medium italic">{section.text}</p>
                      </blockquote>
                    );
                  }
                  if (section.type === "faq" && section.faqs) {
                    return (
                      <div key={index} className="space-y-4 my-8">
                        {section.faqs.map((faq, i) => (
                          <div key={i} className="rounded-2xl border border-border bg-card p-6">
                            <h3 className="font-heading text-lg md:text-xl font-bold text-foreground mb-3">
                              {faq.question}
                            </h3>
                            <p className="text-muted-foreground leading-relaxed">{faq.answer}</p>
                          </div>
                        ))}
                      </div>
                    );
                  }
                  if (section.type === "cta") {
                    return (
                      <div key={index} className="bg-primary/10 border border-primary/20 rounded-2xl p-8 my-12 text-center">
                        <p className="text-foreground font-medium text-lg mb-6">{section.text}</p>
                        <Button asChild variant="cta" size="lg">
                          <Link to="/contact">{t('post.contactNow')}</Link>
                        </Button>
                      </div>
                    );
                  }
                  return null;
                })}
              </motion.div>

              {/* Keywords Tags */}
              <div className="mt-12 pt-8 border-t border-border">
                <h4 className="text-sm font-semibold text-muted-foreground mb-4">{t('post.tags')}</h4>
                <div className="flex flex-wrap gap-2">
                  {translatedPost.keywords.map((keyword, index) => (
                    <span key={index} className="px-3 py-1 text-sm bg-secondary text-secondary-foreground rounded-full">
                      {keyword}
                    </span>
                  ))}
                </div>
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="py-16 bg-secondary/30">
          <div className="container px-4">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground mb-8 text-center">
              {t('post.relatedPosts')}
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              {relatedPosts.map((relatedPost) => {
                const translatedRelated = getTranslatedRelatedPost(relatedPost);
                return (
                  <Link
                    key={relatedPost.id}
                    to={`/blog/${relatedPost.slug}`}
                    className="group bg-card rounded-2xl overflow-hidden border border-border hover:border-accent/30 hover:shadow-xl transition-all duration-300"
                  >
                    <div className="aspect-video overflow-hidden">
                      <img
                        src={relatedPost.image}
                        alt={relatedPost.imageAlt}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        onError={(e) => {
                          const target = e.currentTarget;
                          if (!target.dataset.fallback) {
                            target.dataset.fallback = "true";
                            target.src = getFallbackImageUrl(relatedPost.id, relatedPost.category);
                          }
                        }}
                      />
                    </div>
                    <div className="p-6">
                      <span className="inline-block px-3 py-1 text-xs font-semibold rounded-full bg-accent/10 text-accent mb-3">
                        {translatedRelated.category}
                      </span>
                      <h3 className="font-heading text-lg font-bold text-foreground line-clamp-2 group-hover:text-accent transition-colors">
                        {translatedRelated.title}
                      </h3>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-16 bg-primary">
        <div className="container px-4 text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-primary-foreground mb-6">
            {t('cta.title')}
          </h2>
          <p className="text-lg text-primary-foreground/80 mb-8 max-w-2xl mx-auto">
            {t('cta.description')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild variant="cta" size="lg">
              <Link to="/contact">{t('cta.scheduleCall')}</Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="bg-transparent border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10">
              <Link to="/services">{t('cta.ourServices')}</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Scroll to top button */}
      <button
        onClick={scrollToTop}
        className={`fixed bottom-8 ${isRTL ? 'left-8' : 'right-8'} p-3 bg-accent text-accent-foreground rounded-full shadow-lg hover:scale-110 transition-transform z-50`}
        aria-label={t('scrollToTop')}
      >
        <ChevronUp className="w-6 h-6" />
      </button>
    </Layout>
  );
}
